import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, Input, Label, Table } from "reactstrap";
import ExportJsonExcel from "js-export-excel";
import moment from "moment";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import { muestraFecha, sinFecha } from "../Contenedores/Contenedores/contenedorUtils";

const auth = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const nombre = (arr, campo = "name") => (Array.isArray(arr) && arr[0] ? arr[0][campo] || "" : "");
const doc = (v) => (v === "Si" ? "Sí" : "Pendiente");

// Columnas disponibles para el reporte de estatus (cada cliente guarda las suyas)
const COLUMNAS = [
  { key: "numeroReferencia", label: "Referencia", valor: (c) => c.numeroReferencia },
  { key: "referenciaCliente", label: "Referencia del cliente", valor: (c) => c.referenciaCliente },
  { key: "referencia", label: "Contenedor", valor: (c) => c.referencia },
  { key: "tipoContenedor", label: "Tipo", valor: (c) => c.tipoContenedor },
  { key: "naviera", label: "Naviera", valor: (c) => nombre(c.navieras) },
  { key: "buque", label: "Buque", valor: (c) => (!sinFecha(c.buque) ? c.buque : !sinFecha(c.vessel) ? c.vessel : "") },
  { key: "puerto", label: "Puerto de origen", valor: (c) => (c.puerto !== "NA" ? c.puerto : "") },
  { key: "puertoDestino", label: "Puerto destino", valor: (c) => (c.puertoDestino !== "NA" ? c.puertoDestino : "") },
  { key: "modalidad", label: "Modalidad", valor: (c) => c.modalidad },
  { key: "proveedor", label: "Proveedor", valor: (c) => c.proveedor },
  { key: "booking", label: "Booking", valor: (c) => c.booking },
  { key: "blNumero", label: "BL", valor: (c) => (c.blNumero !== "NA" ? c.blNumero : "") },
  { key: "etd", label: "ETD", valor: (c) => muestraFecha(c.etd) },
  { key: "etaEstimada", label: "ETA estimada", valor: (c) => muestraFecha(c.etaEstimada) },
  { key: "etaReal", label: "ETA actualizada", valor: (c) => muestraFecha(c.etaReal) },
  { key: "etapaViaje", label: "Etapa del viaje", valor: (c) => c.etapaViaje },
  { key: "transbordo", label: "Transbordo", valor: (c) => (c.transbordos || []).map((x) => x.puerto).join(", ") },
  { key: "statusSeguimiento", label: "Estatus México", valor: (c) => c.statusSeguimiento },
  { key: "totalCantidad", label: "Piezas", valor: (c) => c.totalCantidad },
  { key: "broker", label: "Broker", valor: (c) => nombre(c.brokers) },
  { key: "forwarder", label: "Forwarder", valor: (c) => nombre(c.forwarders) },
  { key: "docBL", label: "BL recibido", valor: (c) => doc(c.bl) },
  { key: "docPacking", label: "Packing list", valor: (c) => doc(c.packing) },
  { key: "docFactura", label: "Factura", valor: (c) => doc(c.invoice) },
  { key: "docTelex", label: "Telex / SWB", valor: (c) => doc(c.telex) },
  { key: "fechaEntregaReal", label: "Entrega", valor: (c) => muestraFecha(c.fechaEntregaReal) },
  { key: "observaciones", label: "Observaciones", valor: (c) => (c.observaciones !== "NA" ? c.observaciones : "") },
  { key: "mapa", label: "Mapa", valor: (c) => (c.mapa && c.mapa !== "No" ? c.mapa : "") },
];
const DEFAULT = ["numeroReferencia", "referencia", "naviera", "buque", "etd", "etaReal", "etapaViaje", "statusSeguimiento", "totalCantidad"];

// Reporte de estatus por cliente para descargar, ajustar y enviar a mano
export default function ReporteEstatusCliente() {
  const { user } = useContext(AuthContext);
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState("");
  const [columnas, setColumnas] = useState(DEFAULT);
  const [dias, setDias] = useState(0);
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_URL_CLIENTES, { headers: auth() })
      .then(({ data }) => setClientes((data || []).filter((c) => c.is_active !== "No").sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1))))
      .catch(() => setClientes([]));
  }, []);

  useEffect(() => {
    if (!cliente) return setFilas([]);
    const c = clientes.find((x) => x._id === cliente);
    setColumnas(c && c.columnasReporte && c.columnasReporte.length ? c.columnasReporte : DEFAULT);
    setCargando(true);
    axios
      .get(`${process.env.REACT_APP_URL_CONTENEDORES_CLIENTE}/${cliente}${dias ? `?dias=${dias}` : ""}`, { headers: auth() })
      .then(({ data }) => setFilas(Array.isArray(data) ? data : []))
      .catch(() => setFilas([]))
      .finally(() => setCargando(false));
  }, [cliente, dias]); // eslint-disable-line

  const elegidas = useMemo(() => COLUMNAS.filter((c) => columnas.includes(c.key)), [columnas]);
  const toggleCol = (key) => setColumnas((cs) => (cs.includes(key) ? cs.filter((x) => x !== key) : COLUMNAS.map((c) => c.key).filter((k) => k === key || cs.includes(k))));
  const nombreCliente = (clientes.find((x) => x._id === cliente) || {}).nombre_comercial || "";

  const guardarColumnas = () => {
    axios
      .patch(`${process.env.REACT_APP_URL_CLIENTES}/${cliente}`, { columnasReporte: columnas }, { headers: auth() })
      .then(() => {
        setClientes((cs) => cs.map((c) => (c._id === cliente ? { ...c, columnasReporte: columnas } : c)));
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Columnas guardadas para ${nombreCliente}`, showConfirmButton: false, timer: 1600 });
      })
      .catch((err) => Swal.fire({ icon: "error", title: "No se pudo guardar", text: err?.response?.data?.message || err.message }));
  };

  const excel = () => {
    const datos = filas.map((f) => Object.fromEntries(elegidas.map((c) => [c.label, c.valor(f) ?? ""])));
    const cols = elegidas.map((c) => c.label);
    new ExportJsonExcel({
      fileName: `Estatus ${nombreCliente} ${moment().format("YYYY-MM-DD")}`,
      datas: [{ sheetData: datos, sheetName: "Estatus", sheetFilter: cols, sheetHeader: cols }],
    }).saveExcel();
  };

  if (!user.menuContenedores) return <Header />;

  return (
    <>
      <Header />
      <div className="card container col-sm-11 rc-page">
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <span className="page-title-icon"><i className="fas fa-file-excel" /></span>
              Reporte de estatus por cliente
            </h3>
            <p className="page-subtitle">Elige el cliente y sus columnas; se guardan para la próxima vez. Descarga el Excel, ajústalo y envíalo.</p>
          </div>
          <div className="page-actions">
            <Button size="sm" color="secondary" href="/MenuContenedores"><i className="fas fa-arrow-left" /> Regresar</Button>
            <Button size="sm" color="success" onClick={excel} disabled={!filas.length}><i className="far fa-file-excel" /> Descargar Excel</Button>
          </div>
        </div>

        <div className="filter-bar">
          <div style={{ minWidth: 260 }}>
            <Label>Cliente</Label>
            <Input bsSize="sm" type="select" value={cliente} onChange={(e) => setCliente(e.target.value)}>
              <option value="">Selecciona</option>
              {clientes.map((c) => <option key={c._id} value={c._id}>{c.nombre_comercial}</option>)}
            </Input>
          </div>
          <div>
            <Label>Incluir</Label>
            <Input bsSize="sm" type="select" value={dias} onChange={(e) => setDias(Number(e.target.value))}>
              <option value={0}>Solo abiertos</option>
              <option value={15}>Abiertos + entregados 15 días</option>
              <option value={30}>Abiertos + entregados 30 días</option>
            </Input>
          </div>
        </div>

        {cliente ? (
          <div className="form-section rv-section">
            <div className="form-section-header">
              <i className="fas fa-columns" /><h4 className="form-section-title">Columnas de {nombreCliente}</h4>
              <div style={{ marginLeft: "auto" }}>
                <Button size="sm" color="primary" onClick={guardarColumnas}><i className="fas fa-save" /> Guardar columnas del cliente</Button>
              </div>
            </div>
            <div className="form-section-body">
              <div className="rec-columnas">
                {COLUMNAS.map((c) => (
                  <label key={c.key} className={columnas.includes(c.key) ? "on" : ""}>
                    <input type="checkbox" checked={columnas.includes(c.key)} onChange={() => toggleCol(c.key)} /> {c.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {!cliente ? (
          <div className="rc-state"><i className="fas fa-user" /><div>Elige un cliente</div></div>
        ) : cargando ? (
          <div className="rc-state"><i className="fas fa-spinner fa-spin" /><div>Cargando…</div></div>
        ) : filas.length === 0 ? (
          <div className="rc-state"><i className="fas fa-box-open" /><div>El cliente no tiene contenedores abiertos</div></div>
        ) : (
          <>
            <div className="td-muted" style={{ margin: "6px 0" }}>{filas.length} contenedores · vista previa del Excel</div>
            <Table size="sm" hover className="table-responsive-xl">
              <thead>
                <tr>{elegidas.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr key={f._id}>
                    {elegidas.map((c) => {
                      const v = c.valor(f);
                      return (
                        <td key={c.key} className={v === "" || v === undefined ? "td-muted" : undefined}>
                          {c.key === "mapa" && v ? <a href={v} target="_blank" rel="noopener noreferrer">Ver</a> : v === "" || v === undefined ? "—" : v}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </div>
    </>
  );
}
